import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X as XIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/UI/dialog';
import CodDetailsTableComponent from '../Tables/CodPayableDetails';

/* This sheet borrows the detail-sheet styles the credit/debit note introduced
   (the `cdn-` prefixed rules in tailwind-shadcn-fixes.css): same pinned header,
   same summary cards, same wide line table. Only the pagination footer is COD's
   own, because its consignments are paged by the server. */

const TONES = {
  positive: 'bg-green-50 text-green-700 border-green-200',
  negative: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  neutral: 'bg-gray-100 text-gray-600 border-gray-200',
};

const toneFor = (status) => {
  switch (status) {
    case 'RECONCILED':
    case 'GENERATED':
    case 'COMPLETED':
    case 'PAID':
    case 'SUCCESS':
      return TONES.positive;
    case 'FAILED':
    case 'REJECTED':
    case 'ERROR':
      return TONES.negative;
    case 'PENDING':
    case 'IN_PROGRESS':
    case 'PROCESSING':
      return TONES.pending;
    default:
      return TONES.neutral;
  }
};

const titleCase = (value) =>
  value
    ? String(value)
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    : '';

const formatDate = (value) =>
  value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : '-';

/* Archive timestamps like reconciledAt arrive as numeric strings, and an
   unreconciled row carries 0 rather than null, which would read as 1970. */
const formatTimestamp = (value) => {
  const ms = Number(value);
  return ms > 0 ? dayjs(ms).format('DD MMM YYYY, hh:mm A') : '-';
};

const isPresent = (value) =>
  value !== null && value !== undefined && value !== '';

const formatAmount = (value) =>
  value === null || value === undefined || value === ''
    ? '-'
    : Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const ADJUSTMENT_TYPES = {
  CN: 'Credit Note',
  DN: 'Debit Note',
};

const signedAmount = (value) => {
  const amount = toNumber(value);
  return `${amount > 0 ? '+' : amount < 0 ? '-' : ''}${formatAmount(Math.abs(amount))}`;
};

const amountToneClass = (value) => {
  const amount = toNumber(value);
  return amount > 0 ? 'is-positive' : amount < 0 ? 'is-negative' : '';
};

const toNumber = (value) =>
  value === null || value === undefined || value === '' ? 0 : Number(value) || 0;

const CodPayableDetailModal = ({
  open,
  onClose,
  summary,
  details,
  isLoading,
  isSummaryLoading,
  page,
  size,
  hasNext,
  onPreviousPage,
  onNextPage,
  onSizeChange,
  /* 'payable' for a freshly generated payable, 'archive' for an archived
     summary row, which says who generated, emailed and reconciled it but
     carries no COD-collected / AJEX-charges breakdown. */
  variant = 'payable',
}) => {
  const isArchive = variant === 'archive';

  /* The details endpoint returns its consignments under `records`; the older
     shape called them `consignments`, so both are read. */
  const consignments = useMemo(
    () => details?.records ?? details?.consignments ?? [],
    [details]
  );

  /* The account and the name live on the consignments, not on the envelope
     around them, so they are read off the first row the page carries. */
  const firstRow = consignments[0];

  const customerAccount =
    summary?.customerAccount || summary?.customer || firstRow?.customerAccount || '-';
  const customerName = summary?.customerName || firstRow?.customerName || '';

  /* A payable can carry any number of adjustment entries, each a credit or
     debit note against it; they are already folded into the payable amount, so
     the card states them rather than adding them to anything. */
  const adjustments = useMemo(() => summary?.adjustments ?? [], [summary]);
  const adjustmentsTotal = useMemo(
    () => adjustments.reduce((total, entry) => total + toNumber(entry?.amount), 0),
    [adjustments]
  );

  /* The payable summary carries no currency of its own, so it is read off the
     consignments it was built from. */
  const currency = useMemo(() => {
    const row = consignments.find(
      (c) => c?.customerBillingCurrency || c?.billingCurrency
    );
    return row?.customerBillingCurrency || row?.billingCurrency || '';
  }, [consignments]);

  /* The collected → payable strip and the difference are only drawn when the
     summary actually has a collected figure; archive rows do not. */
  const hasBreakdown = isPresent(summary?.codCollected);
  const destinationCountry =
    summary?.destinationCountry ||
    summary?.destinationCountryCode ||
    firstRow?.destinationCountryCode;

  const collected = toNumber(summary?.codCollected);
  const ajexCharges = toNumber(summary?.ajexCharges);
  const payable = toNumber(summary?.payable);
  const bankCharges = toNumber(summary?.bankCharges);
  const difference = payable - collected;

  /* The consignments are paged by the server, so the strip under the table can
     only total the rows of the page currently on screen. */
  const pageTotals = useMemo(
    () =>
      consignments.reduce(
        (acc, c) => ({
          cod: acc.cod + toNumber(c.codConvertedAmount ?? c.codAmount),
          codCharges: acc.codCharges + toNumber(c.codCharges),
          charges: acc.charges + toNumber(c.totalCharge),
        }),
        { cod: 0, codCharges: 0, charges: 0 }
      ),
    [consignments]
  );

  const rangeNote = summary?.from
    ? `${dayjs(summary.from).format('DD MMM YYYY')} – ${
        summary?.to ? dayjs(summary.to).format('DD MMM YYYY') : '-'
      }`
    : '';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="cdn-detail-dialog bg-white" onClose={onClose}>
        <DialogHeader className="cdn-sheet-header cdn-sheet-pad">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="cdn-sheet-eyebrow">
                Finance · {isArchive ? 'COD Archive' : 'COD Payable'}
              </p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <DialogTitle asChild>
                  <h1 className="cdn-sheet-title">{customerAccount}</h1>
                </DialogTitle>
                {customerName && (
                  <span className="text-sm text-gray-500">{customerName}</span>
                )}
                {summary?.status && (
                  <Badge
                    tone={toneFor(summary.status)}
                    label={titleCase(summary.status)}
                  />
                )}
                {summary?.deductible && (
                  <Badge tone={TONES.info} label="Deductible" />
                )}
              </div>
            </div>

            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="cdn-sheet-close shrink-0"
            >
              <XIcon className="size-4" aria-hidden="true" />
            </button>
          </div>
        </DialogHeader>

        <div className="cdn-detail-body cdn-sheet-pad">
          {/* ── Summary ─────────────────────────────────────────────────── */}
          <section className="cdn-section">
            <SectionTitle note={rangeNote}>Summary</SectionTitle>

            <div className="cdn-summary-grid">
              <div className="cdn-card cdn-hero">
                <p className="cdn-card-eyebrow">Payable Amount</p>
                <div className="cdn-hero-figure">
                  <span className="cdn-hero-amount">{formatAmount(summary?.payable)}</span>
                  {currency && <span className="cdn-hero-currency">{currency}</span>}
                </div>
                {hasBreakdown ? (
                  <p className="cdn-hero-sub">
                    COD collected {formatAmount(summary?.codCollected)} · AJEX charges{' '}
                    {formatAmount(summary?.ajexCharges)} · bank charges{' '}
                    {formatAmount(summary?.bankCharges)}
                  </p>
                ) : (
                  <p className="cdn-hero-sub">
                    Bank charges {formatAmount(summary?.bankCharges)}
                    {summary?.generatedOn &&
                      ` · generated ${dayjs(summary.generatedOn).format('DD MMM YYYY')}`}
                    {summary?.generatedBy && ` by ${summary.generatedBy}`}
                  </p>
                )}

                {hasBreakdown ? (
                  <div className="cdn-oldnew">
                    <div className="cdn-oldnew-cell">
                      <p className="cdn-oldnew-label">COD collected</p>
                      <p className="cdn-oldnew-value">{formatAmount(collected)}</p>
                    </div>
                    <ArrowRight className="cdn-oldnew-arrow" aria-hidden="true" />
                    <div className="cdn-oldnew-cell">
                      <p className="cdn-oldnew-label">Payable</p>
                      <p className="cdn-oldnew-value">{formatAmount(payable)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="cdn-oldnew">
                    <div className="cdn-oldnew-cell">
                      <p className="cdn-oldnew-label">Generated on</p>
                      <p className="cdn-oldnew-value">{formatDate(summary?.generatedOn)}</p>
                    </div>
                    <ArrowRight className="cdn-oldnew-arrow" aria-hidden="true" />
                    <div className="cdn-oldnew-cell">
                      <p className="cdn-oldnew-label">Reconciled at</p>
                      <p className="cdn-oldnew-value">
                        {formatTimestamp(summary?.reconciledAt)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="cdn-hero-split cod-hero-split">
                  {hasBreakdown ? (
                    <>
                      <div>
                        <p className="cdn-oldnew-label">Difference</p>
                        <p
                          className={`cdn-hero-delta ${
                            difference > 0 ? 'is-up' : difference < 0 ? 'is-down' : 'is-flat'
                          }`}
                        >
                          {difference > 0 ? '+' : difference < 0 ? '-' : ''}
                          {formatAmount(Math.abs(difference))}
                        </p>
                      </div>
                      <div>
                        <p className="cdn-oldnew-label">AJEX charges</p>
                        <p className="cdn-hero-delta is-flat">{formatAmount(ajexCharges)}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="cdn-oldnew-label">Email sent</p>
                        <p className="cdn-hero-delta is-flat">
                          {summary?.emailSent ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="cdn-oldnew-label">Reconciled by</p>
                        <p className="cdn-hero-delta is-flat">{summary?.reconciledBy || '-'}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <p className="cdn-oldnew-label">Bank charges</p>
                    <p className="cdn-hero-delta is-flat">{formatAmount(bankCharges)}</p>
                  </div>
                </div>
              </div>

              <div className="cdn-card">
                <p className="cdn-card-eyebrow">Reference</p>
                <div className="cdn-dl">
                  <DetailRow label="Customer Account" value={customerAccount} />
                  <DetailRow label="Customer Name" value={customerName} />
                  <DetailRow label="Destination Country" value={destinationCountry} />
                  <DetailRow label="From Date" value={formatDate(summary?.from)} />
                  <DetailRow label="To Date" value={formatDate(summary?.to)} />
                  {isArchive && (
                    <DetailRow label="Generated By" value={summary?.generatedBy} />
                  )}
                </div>
              </div>

              <div className="cdn-card">
                <p className="cdn-card-eyebrow">Status</p>
                <div className="cdn-dl">
                  <DetailRow label="Status">
                    {summary?.status ? (
                      <Badge
                        tone={toneFor(summary.status)}
                        label={titleCase(summary.status)}
                      />
                    ) : (
                      '-'
                    )}
                  </DetailRow>
                  <DetailRow label="Email Sent" value={summary?.emailSent ? 'Yes' : 'No'} />
                  {isArchive ? (
                    <>
                      <DetailRow label="Email Sent By" value={summary?.emailSentBy} />
                      <DetailRow label="Generated" value={summary?.generated ? 'Yes' : 'No'} />
                      <DetailRow label="Reconciled By" value={summary?.reconciledBy} />
                    </>
                  ) : (
                    <>
                      <DetailRow label="Deductible">
                        <Badge
                          tone={summary?.deductible ? TONES.info : TONES.neutral}
                          label={summary?.deductible ? 'Yes' : 'No'}
                        />
                      </DetailRow>
                      <DetailRow label="Disabled" value={summary?.disable ? 'Yes' : 'No'} />
                      <DetailRow
                        label="Pending Payable"
                        value={
                          isPresent(summary?.pendingPayable)
                            ? formatAmount(summary.pendingPayable)
                            : '-'
                        }
                      />
                    </>
                  )}
                  <DetailRow label="Summary Id" value={summary?.summaryId} />
                </div>
              </div>
              {(isSummaryLoading || adjustments.length > 0) && (
                <div className="cdn-card cod-adj-card">
                  <div className="cod-adj-head">
                    <p className="cdn-card-eyebrow cod-adj-eyebrow">
                      Adjustments{adjustments.length > 0 ? ` · ${adjustments.length}` : ''}
                    </p>
                    {adjustments.length > 0 && (
                      <span className="cod-adj-sum">
                        Total
                        <strong className={amountToneClass(adjustmentsTotal)}>
                          {signedAmount(adjustmentsTotal)}
                        </strong>
                      </span>
                    )}
                  </div>

                  {isSummaryLoading ? (
                    <span className="cod-adj-empty flex items-center gap-2">
                      <Loader2 className="size-3.5 animate-spin" />
                      Loading adjustments
                    </span>
                  ) : (
                    <div className="cod-adj-list">
                      {adjustments.map((entry, i) => (
                        <div className="cod-adj-item" key={`${entry?.type}-${i}`}>
                          <span className="cod-adj-meta">
                            <Badge
                              tone={entry?.type === 'CN' ? TONES.info : TONES.pending}
                              label={ADJUSTMENT_TYPES[entry?.type] || entry?.type || 'Adjustment'}
                            />
                            <span
                              className="cod-adj-remarks"
                              title={entry?.remarks || ''}
                            >
                              {entry?.remarks || '-'}
                            </span>
                          </span>
                          <span className={`cod-adj-amount ${amountToneClass(entry?.amount)}`}>
                            {signedAmount(entry?.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ── Consignments ────────────────────────────────────────────── */}
          <section className="cdn-section">
            <SectionTitle>Consignments</SectionTitle>

            <div className="rounded-lg border border-gray-200 border-solid overflow-hidden">
              {/* The column headers stay on screen whatever the state of the
                  rows, so the sheet always says what it is about to show. */}
              <div className="cdn-line-scroll w-full overflow-x-auto overflow-y-auto">
                <table className="cdn-line-table min-w-full w-full text-sm">
                  <CodDetailsTableComponent
                    tableData={consignments}
                    isLoading={isLoading}
                    showPlaceholderRow={false}
                  />
                </table>
              </div>

              {/* A row spanning 37 columns puts its message wherever the table
                  happens to be panned, so the loading and empty states are
                  drawn under the scroller instead, where they cannot drift. */}
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 border-t border-gray-200 border-solid bg-white py-12 text-gray-500">
                  <Loader2 className="size-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : consignments.length === 0 ? (
                <div className="border-t border-gray-200 border-solid bg-white py-12 text-center text-sm text-gray-500">
                  No records found
                </div>
              ) : (
                <div className="cdn-lines-total">
                  <Total label="COD converted amt" value={formatAmount(pageTotals.cod)} />
                  <Total label="COD charges" value={formatAmount(pageTotals.codCharges)} />
                  <Total label="Total charges" value={formatAmount(pageTotals.charges)} />
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Consignments are paged by the server, so the controls stay pinned
            below the scrolling body. */}
        <div className="cod-sheet-footer">
          {isLoading && (
            <span className="flex items-center gap-2 text-xs text-gray-500 mr-auto">
              <Loader2 className="size-3.5 animate-spin" />
              Loading
            </span>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Rows per page:</span>
            <select
              value={size}
              onChange={(e) => onSizeChange(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EC171C] focus-visible:ring-offset-1"
              disabled={isLoading}
            >
              {[10, 20, 50, 100].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPreviousPage}
              disabled={page === 0 || isLoading}
              className="!p-1.5 rounded-md !border border-gray-300 border-solid bg-white hover:!bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="size-4 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={onNextPage}
              disabled={!hasNext || isLoading}
              className="!p-1.5 rounded-md !border border-gray-300 border-solid bg-white hover:!bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="size-4 text-gray-600" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SectionTitle = ({ children, note }) => (
  <div className="cdn-section-head">
    <h2 className="cdn-section-title">{children}</h2>
    {note && <p className="cdn-section-note">{note}</p>}
  </div>
);

const DetailRow = ({ label, value, children }) => {
  const display = value === 0 ? '0' : value || '-';
  return (
    <div className="cdn-dl-row">
      <span className="cdn-dl-label">{label}</span>
      <span className="cdn-dl-value">
        {children ?? (
          <span className="cdn-truncate" title={String(display)}>
            {display}
          </span>
        )}
      </span>
    </div>
  );
};

const Total = ({ label, value, strong }) => (
  <span className="cdn-total">
    <span className="cdn-total-label">{label}</span>
    <span className={`cdn-total-value ${strong ? 'is-strong' : ''}`}>{value}</span>
  </span>
);

const Badge = ({ tone, label }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded border border-solid text-xs font-medium whitespace-nowrap ${tone}`}
  >
    {label}
  </span>
);

export default CodPayableDetailModal;
